import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Card,
  Select,
  Input,
  Button,
  Space,
  DatePicker,
  Tag,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Divider,
  Badge,
  Tooltip,
} from "antd";
import {
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileZipOutlined,
  FilterOutlined,
  EyeOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import config from "../../config";
import dayjs from "dayjs";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { getRepositorioSoportesByProyecto } from "../../services/indicadoresPO/indicadoresService";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const RepositorioSoportesTable = ({ oidProyecto }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filters, setFilters] = useState({
    proyecto: "",
    implementador: "",
    factor: "",
    medioVerificacion: "",
    tipoActividad: "",
    fechaRange: null,
    searchText: "",
  });

  const baseUrl = config.baseUrl;

  // Cargar datos
  const fetchData = async () => {
    if (!oidProyecto) return;

    setLoading(true);
    try {
      const response = await getRepositorioSoportesByProyecto(oidProyecto);
      if (response.status === 200) {
        const dataWithKeys = response.data.map((item, index) => ({
          ...item,
          key: index,
        }));
        setData(dataWithKeys);
      }
    } catch (error) {
      message.error("Error al cargar los datos");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [oidProyecto]);

  // Obtener valores únicos para los filtros
  const filterOptions = useMemo(() => {
    const getUniqueValues = (key) =>
      [...new Set(data.map((item) => item[key]))].filter(Boolean);

    return {
      proyectos: getUniqueValues("codigo_proyecto"),
      implementadores: getUniqueValues("implementador"),
      factores: getUniqueValues("fase"),
      mediosVerificacion: getUniqueValues("medio_verif_catalogo"),
      tiposActividad: getUniqueValues("tipo_actividad"),
      actores: getUniqueValues("actor"),
    };
  }, [data]);

  // Filtrar datos
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchProyecto =
        !filters.proyecto || item.codigo_proyecto === filters.proyecto;
      const matchImplementador =
        !filters.implementador || item.implementador === filters.implementador;
      const matchFactor = !filters.factor || item.fase === filters.factor;
      const matchMedioVerif =
        !filters.medioVerificacion ||
        item.medio_verif_catalogo === filters.medioVerificacion;
      const matchTipoActividad =
        !filters.tipoActividad || item.tipo_actividad === filters.tipoActividad;

      const matchDateRange =
        !filters.fechaRange ||
        (dayjs(item.fecha_soporte).isAfter(filters.fechaRange[0]) &&
          dayjs(item.fecha_soporte).isBefore(filters.fechaRange[1]));

      const matchSearch =
        !filters.searchText ||
        Object.values(item).some(
          (value) =>
            value &&
            value
              .toString()
              .toLowerCase()
              .includes(filters.searchText.toLowerCase())
        );

      return (
        matchProyecto &&
        matchImplementador &&
        matchFactor &&
        matchMedioVerif &&
        matchTipoActividad &&
        matchDateRange &&
        matchSearch
      );
    });
  }, [data, filters]);

  // Función para abrir archivo en nueva ventana
  const openFileInNewWindow = (rutaSoporte) => {
    const fullUrl = `${baseUrl}${rutaSoporte}`;
    window.open(fullUrl, "_blank");
  };

  // Función para descargar archivos
  const downloadSelectedFiles = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Por favor selecciona al menos un archivo");
      return;
    }

    const selectedFiles = selectedRowKeys.map((key) => {
      const item = filteredData.find((d) => d.key === key);
      return {
        name: item.ruta_soporte.split("/").pop(),
        url: `${baseUrl}${item.ruta_soporte}`,
        rutaSoporte: item.ruta_soporte,
        item: item,
      };
    });

    if (selectedFiles.length === 1) {
      // Descargar un solo archivo
      const file = selectedFiles[0];
      try {
        message.success(`Descargando: ${file.name}`);
        // Crear elemento de descarga temporal
        const link = document.createElement("a");
        link.href = file.url;
        link.download = file.name;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        message.error("Error al descargar el archivo");
        console.error("Error downloading file:", error);
      }
    } else {
      // Crear ZIP para múltiples archivos
      try {
        const loadingMessage = message.loading(
          "Preparando archivos para descarga...",
          0
        );

        const zip = new JSZip();
        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        // Agregar archivos al ZIP
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          try {
            // Actualizar mensaje de progreso
            loadingMessage();
            message.loading(
              `Descargando archivo ${i + 1} de ${selectedFiles.length}: ${
                file.name
              }`,
              0
            );

            const response = await fetch(file.url);

            if (!response.ok) {
              throw new Error(
                `HTTP ${response.status}: ${response.statusText}`
              );
            }

            const blob = await response.blob();

            // Verificar que el blob no esté vacío
            if (blob.size === 0) {
              throw new Error("Archivo vacío");
            }

            // Crear nombre único si hay duplicados
            let fileName = file.name;
            let counter = 1;
            while (zip.file(fileName)) {
              const nameWithoutExt =
                file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
              const extension =
                file.name.substring(file.name.lastIndexOf(".")) || "";
              fileName = `${nameWithoutExt}_${counter}${extension}`;
              counter++;
            }

            zip.file(fileName, blob);
            successCount++;
          } catch (error) {
            errorCount++;
            const errorMessage = `${file.name}: ${error.message}`;
            errors.push(errorMessage);
            console.error(`Error al agregar ${file.name} al ZIP:`, error);
          }
        }

        message.destroy();

        if (successCount === 0) {
          message.error(
            "No se pudo descargar ningún archivo. Verifica las URLs y la conectividad."
          );
          if (errors.length > 0) {
            console.error("Errores detallados:", errors);
          }
          return;
        }

        // Generar y descargar ZIP
        message.loading("Generando archivo ZIP...", 0);

        const zipBlob = await zip.generateAsync({
          type: "blob",
          compression: "DEFLATE",
          compressionOptions: { level: 6 },
        });

        // Crear nombre de archivo con timestamp y proyecto
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[-:T]/g, "");
        const proyectoCode = selectedFiles[0]?.item?.codigo_proyecto || "DOCS";
        const zipFileName = `${proyectoCode}_documentos_${timestamp}.zip`;

        saveAs(zipBlob, zipFileName);

        message.destroy();

        // Mostrar resumen de resultados
        if (errorCount === 0) {
          message.success(
            `✅ ZIP descargado exitosamente con ${successCount} archivos`
          );
        } else {
          message.warning(
            `⚠️ ZIP descargado con ${successCount} archivos. ${errorCount} archivos fallaron.`,
            5
          );
          // Mostrar errores en consola para debug
          if (errors.length > 0) {
            console.warn("Archivos que fallaron:", errors);
          }
        }
      } catch (error) {
        message.destroy();
        message.error("Error al crear el archivo ZIP");
        console.error("Error creating ZIP:", error);
      }
    }
  };

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: "Proyecto",
      dataIndex: "codigo_proyecto",
      key: "codigo_proyecto",
      width: 120,
      fixed: "left",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Implementador",
      dataIndex: "implementador",
      key: "implementador",
      width: 150,
    },
    {
      title: "Factor",
      dataIndex: "fase_sigla",
      key: "fase_sigla",
      width: 150,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Actividad",
      dataIndex: "actividad",
      key: "actividad",
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <div title={text} style={{ maxWidth: 280 }}>
          {text}
        </div>
      ),
    },
    {
      title: "Indicador",
      dataIndex: "indicador",
      key: "indicador",
      width: 250,
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: "Tipo Actividad",
      dataIndex: "tipo_actividad",
      key: "tipo_actividad",
      width: 120,
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: "Medio Verificación",
      dataIndex: "medio_verif_catalogo",
      key: "medio_verif_catalogo",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Meta",
      dataIndex: "meta",
      key: "meta",
      width: 80,
      align: "center",
    },
    {
      title: "Meta Ejecutada",
      dataIndex: "meta_ejecutada",
      key: "meta_ejecutada",
      width: 120,
      align: "center",
      render: (text, record) => (
        <Badge
          count={`${text}/${record.meta}`}
          style={{
            backgroundColor: text === record.meta ? "#52c41a" : "#faad14",
          }}
        />
      ),
    },
    {
      title: "Avance %",
      dataIndex: "avance_individual_pct",
      key: "avance_individual_pct",
      width: 100,
      align: "center",
      render: (text) => `${(parseFloat(text) * 100).toFixed(0)}%`,
    },
    {
      title: "Fecha Soporte",
      dataIndex: "fecha_soporte",
      key: "fecha_soporte",
      width: 120,
      render: (text) => dayjs(text).format("DD/MM/YYYY"),
    },
    {
      title: "Mes POA",
      dataIndex: "mes_poa",
      key: "mes_poa",
      width: 100,
      align: "center",
    },
    {
      title: "Creado Por",
      dataIndex: "creado_por",
      key: "creado_por",
      width: 120,
    },
    {
      title: "Observaciones",
      dataIndex: "observaciones",
      key: "observaciones",
      width: 250,
      ellipsis: true,
    },
    {
      title: "Archivo",
      dataIndex: "ruta_soporte",
      key: "archivo",
      width: 120,
      align: "center",
      fixed: "right",
      render: (rutaSoporte, record) => (
        <Space>
          <Tooltip title="Ver archivo en nueva ventana">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => openFileInNewWindow(rutaSoporte)}
              disabled={!rutaSoporte}
            />
          </Tooltip>
          <Tooltip title={`${baseUrl}${rutaSoporte}`}>
            <Button
              type="default"
              icon={<LinkOutlined />}
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(`${baseUrl}${rutaSoporte}`);
                message.success("URL copiada al portapapeles");
              }}
              disabled={!rutaSoporte}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: !record.ruta_soporte,
      name: record.codigo_tarea,
    }),
  };

  const clearFilters = () => {
    setFilters({
      proyecto: "",
      implementador: "",
      factor: "",
      medioVerificacion: "",
      tipoActividad: "",
      fechaRange: null,
      searchText: "",
    });
  };

  return (
    <div style={{ padding: "20px" }}>
      <Card>
        <div style={{ marginBottom: "20px" }}>
          <Text type="secondary">
            Gestión de documentos de soporte por proyecto
          </Text>
        </div>

        {/* Filtros */}
        <Card size="small" style={{ marginBottom: "20px" }}>
          <Row gutter={[16, 16]}>
            {/*
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Proyecto"
                value={filters.proyecto}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, proyecto: value }))
                }
                style={{ width: "100%" }}
                allowClear
              >
                {filterOptions.proyectos.map((proyecto) => (
                  <Option key={proyecto} value={proyecto}>
                    {proyecto}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Implementador"
                value={filters.implementador}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, implementador: value }))
                }
                style={{ width: "100%" }}
                allowClear
              >
                {filterOptions.implementadores.map((impl) => (
                  <Option key={impl} value={impl}>
                    {impl}
                  </Option>
                ))}
              </Select>
            </Col>
            */}

            <Col xs={24} sm={12} md={8}>
              <Typography.Text strong>Factor</Typography.Text>
              <Select
                placeholder="Factor"
                value={filters.factor}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, factor: value }))
                }
                style={{ width: "100%", marginTop: 4 }}
                allowClear
              >
                {filterOptions.factores.map((factor) => (
                  <Option key={factor} value={factor}>
                    {factor}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Typography.Text strong>Medio Verificación</Typography.Text>

              <Select
                placeholder="Medio Verificación"
                value={filters.medioVerificacion}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, medioVerificacion: value }))
                }
                style={{ width: "100%" }}
                allowClear
              >
                {filterOptions.mediosVerificacion.map((medio) => (
                  <Option key={medio} value={medio}>
                    {medio}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Typography.Text strong>Tipo Actividad</Typography.Text>

              <Select
                placeholder="Tipo Actividad"
                value={filters.tipoActividad}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, tipoActividad: value }))
                }
                style={{ width: "100%" }}
                allowClear
              >
                {filterOptions.tiposActividad.map((tipo) => (
                  <Option key={tipo} value={tipo}>
                    {tipo}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <RangePicker
                placeholder={["Fecha inicio", "Fecha fin"]}
                value={filters.fechaRange}
                onChange={(dates) =>
                  setFilters((prev) => ({ ...prev, fechaRange: dates }))
                }
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Buscar en todos los campos..."
                value={filters.searchText}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    searchText: e.target.value,
                  }))
                }
                prefix={<SearchOutlined />}
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Button onClick={clearFilters} style={{ width: "100%" }}>
                Limpiar Filtros
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Acciones */}
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={downloadSelectedFiles}
              disabled={selectedRowKeys.length === 0}
            >
              {selectedRowKeys.length > 1 ? (
                <>
                  <FileZipOutlined /> Descargar ZIP ({selectedRowKeys.length})
                </>
              ) : (
                `Descargar (${selectedRowKeys.length})`
              )}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
          <div>
            <Text type="secondary">
              {selectedRowKeys.length > 0 &&
                `${selectedRowKeys.length} seleccionados de `}
              {filteredData.length} registros
              {filteredData.length !== data.length && ` (${data.length} total)`}
            </Text>
          </div>
        </div>

        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowSelection={rowSelection}
          loading={loading}
          scroll={{ x: 2000, y: 600 }}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} registros`,
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          size="small"
          bordered
        />
      </Card>
    </div>
  );
};

export default RepositorioSoportesTable;
